import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { country } from "@flint/api-contract";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@flint/api/router";
import { useNavigate } from "@tanstack/react-router";
import {
  Button,
  ConfirmDialog,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@flint/ui";
import { ProductImagesField } from "./product-images-field.tsx";
import { NewOptionDialog, type OptionKind } from "./new-option-dialog.tsx";
import {
  optionsForDialog,
  useCatalogOptions,
  useSubCategoryReset,
} from "./use-catalog-options.ts";
import { ProductPackFields } from "./product-pack-fields.tsx";
import { perPiecePaise } from "../price-math.ts";
import {
  MAX_PACKS,
  effectivePiecesPerPack,
  entriesFromListing,
  pricingEntryFormSchema,
  usableEntries,
} from "./size-ratio.ts";
import { AddOptionButton, StoreFormRow, StoreFormSection } from "./store-form-section.tsx";
import {
  CARE_OPTIONS,
  COUNTRY_OPTIONS,
  CARE_VALUES,
  TEXTAREA_CLASS,
  buildAttributes,
  normalizeCare,
  normalizeCountryOfOrigin,
} from "./product-form-constants.ts";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  TextField,
  SelectField,
} from "@flint/ui/forms";
import { publishBlockers, type Visibility } from "./listing-status.ts";
import { StatusSegments } from "./status-segments.tsx";
import { AttributeBuilder } from "./attribute-builder.tsx";
import { VariantsField, variantsFromWire, variantsToWire } from "./variants-field.tsx";
import {
  completeAxes,
  variantAttributesFormSchema,
  variantsFormSchema,
} from "./variant-form.ts";
import { useListingVisibility } from "./use-listing-visibility.ts";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ProductWizardStepper } from "./product-wizard-stepper.tsx";
import { ProductReviewStep } from "./product-review-step.tsx";
import { ProductPreviewRail } from "./product-preview-rail.tsx";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(255),
  // Display-only — the field is disabled and never sent on update. It lives in
  // the form purely so Identity reads in the same order as the create form.
  sku: z.string().trim(),
  hsn: z.string().trim().max(8),
  // Required, same as the create form — "Category *" has to mean it.
  category: z.string().trim().min(1, "Category is required").max(48),
  // Optional where the parent is required — see the create form's note.
  subCategory: z.string().trim().max(48),
  brand: z.string().trim().max(96),
  // Closed set, same enum the server validates against. Blank stays blank here
  // — see the note in `toValues`.
  countryOfOrigin: country.or(z.literal("")),
  description: z.string().trim().max(500),
  // Same range guard as the create form — `taxRateBps` caps at 10_000 (100%).
  taxPct: z
    .string()
    .trim()
    .refine((v) => v === "" || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100), {
      message: "Enter a GST rate between 0 and 100",
    }),
  piecesPerPack: z.string().trim(),
  sizeRatio: z.string().trim().max(64),
  // Shared with the create form and the picker's own type — see
  // `pricingEntryFormSchema`. A private copy here dropped `packId` (#196).
  pricingEntries: z.array(pricingEntryFormSchema).max(MAX_PACKS),
  // The variant matrix — fragments shared with the create form (@see variant-form.ts).
  variantAttributes: variantAttributesFormSchema,
  variants: variantsFormSchema,
  shortDesc: z.string().trim().max(500),
  longDesc: z.string().trim().max(4000),
  material: z.string().trim().max(120),
  gsm: z.string().trim().max(120),
  colour: z.string().trim().max(120),
  collection: z.string().trim().max(120),
  // Closed set (blank = not specified). Free text before — see CARE_VALUES.
  care: z.enum(CARE_VALUES).or(z.literal("")),
});
type Values = z.infer<typeof schema>;

/**
 * Same sentence the create form and `publishListing` use — one rule, one
 * wording, whichever control refused the operator. @see NEEDS_PRICE there.
 */
const NEEDS_PRICE = "Add at least one pack price";

/**
 * The edit wizard's steps (user decision 2026-08-13: edit matches the create
 * wizard). Same cards as before, one per step — minus Inventory (opening
 * stock is a create-time posting, not an editable property) and with the
 * staged status + Save moved into Review & Save.
 */
const EDIT_WIZARD_STEPS = [
  {
    key: "basic",
    label: "Basic information",
    description: "Name, HSN, category — the SKU is immutable once used on an order.",
  },
  { key: "images", label: "Images", description: "Up to 8 photos; the first is the primary." },
  {
    key: "attributes",
    label: "Attributes",
    description: "The option axes this product comes in — Size, Color, Material…",
  },
  {
    key: "variants",
    label: "Variants",
    description: "Every attribute combination as a row you can fine-tune.",
  },
  {
    key: "bundles",
    label: "Bundle Builder",
    description: "Compose and price the fixed packages a dealer buys.",
  },
  {
    key: "merchandising",
    label: "Merchandising",
    description: "Copy and attributes shown on the line sheet and product page.",
  },
  {
    key: "review",
    label: "Review & Save",
    description: "Check everything, pick the status, and save.",
  },
] as const;

/** Fields each step validates on Next — full validation still runs at save. */
const EDIT_STEP_FIELDS: (keyof Values)[][] = [
  ["name", "sku", "hsn", "category", "subCategory", "collection", "description"],
  [],
  ["variantAttributes"],
  ["variants"],
  ["pricingEntries", "taxPct", "piecesPerPack", "sizeRatio"],
  ["shortDesc", "longDesc", "brand", "countryOfOrigin", "material", "gsm", "colour", "care"],
];

/** Field → step, for submit-error jumps and the review card links. */
const EDIT_FIELD_STEP: Record<string, number> = {
  name: 0,
  sku: 0,
  hsn: 0,
  category: 0,
  subCategory: 0,
  collection: 0,
  description: 0,
  variantAttributes: 2,
  variants: 3,
  pricingEntries: 4,
  taxPct: 4,
  piecesPerPack: 4,
  sizeRatio: 4,
  shortDesc: 5,
  longDesc: 5,
  brand: 5,
  countryOfOrigin: 5,
  material: 5,
  gsm: 5,
  colour: 5,
  care: 5,
};

/**
 * Is there something to price this product at? Mirrors the create form: one
 * priced entry is enough, with a legacy positional run as the only escape
 * hatch. Not `pieces > 0` — the size grid is optional, so an entry may be
 * priced without a composition.
 *
 * Not a schema refinement, deliberately. As one it sat in the resolver and
 * refused *every* save of a priceless product — including a draft the operator
 * was only renaming, which AC9 says must go through. A live product does still
 * need a price, so the caller applies this by visibility rather than always.
 */
function hasPricing(v: Values): boolean {
  if (usableEntries(v.pricingEntries).length > 0) return true;
  return effectivePiecesPerPack(v.sizeRatio, v.piecesPerPack) > 0;
}

type Product = inferRouterOutputs<AppRouter>["storeCatalog"]["get"];

/** The saved product as form values. Paise → rupees, bps → percent. */
function toValues(p: Product): Values {
  // The rate is banked per piece but a pack is edited as a pack, so multiply
  // back up for display — the inverse of what the submit path divides. A pack
  // price that didn't divide evenly reappears a paisa or two off, which is the
  // honest reading: it is what the record actually holds.
  const stored = p.basePricePaise ? BigInt(p.basePricePaise) : 0n;
  const shownPaise =
    p.soldBy === "pack" && p.piecesPerPack && p.piecesPerPack > 0
      ? stored * BigInt(p.piecesPerPack)
      : stored;
  return {
    name: p.name,
    sku: p.sku ?? "",
    hsn: p.hsn ?? "",
    category: p.category ?? "",
    subCategory: p.subCategory ?? "",
    brand: p.brand ?? "",
    // Deliberately NOT defaulted to India, unlike the create form. A saved null
    // means nobody has said; filling it in here would put a country of origin on
    // a product whose operator only came to fix a typo, and that value ends up
    // on export paperwork. Blank until someone chooses.
    countryOfOrigin: normalizeCountryOfOrigin(p.countryOfOrigin),
    description: p.description ?? "",
    taxPct: p.taxRateBps === null ? "" : (p.taxRateBps / 100).toString(),
    // No "6" fallback: the count is derived from the composition now, and a
    // fabricated default would mask a listing that never had one.
    piecesPerPack: p.piecesPerPack?.toString() ?? "",
    sizeRatio: p.sizeRatio ?? "",
    // Rows saved before ADR-0051 have no `kind` — every one was a pack. Rows
    // from before entries existed describe their one way to buy through the
    // columns, priced at `rate × pieces`. A legacy positional run yields no
    // entries and stays on `sizeRatio`, where the form shows it rather than
    // guessing a composition.
    pricingEntries: entriesFromListing(p.pricingEntries, p.soldBy, p.sizeRatio, p.piecesPerPack, {
      pricePaise: shownPaise.toString(),
      discountBps: p.discountBps,
    }),
    variantAttributes: p.variantAttributes,
    variants: variantsFromWire(p.variants),
    shortDesc: p.shortDesc ?? "",
    longDesc: p.longDesc ?? "",
    material: p.attributes.material ?? "",
    gsm: p.attributes.gsm ?? "",
    colour: p.attributes.colour ?? "",
    // Always empty: the field adds a membership, it does not represent them
    // (spec 15 R5 — a style sits in many line sheets and this control holds
    // one). Prefilling it would read as "the collection", so changing it would
    // read as a move, and the save would silently produce dual membership.
    collection: "",
    // Normalised here, not at submit: the schema is an enum, so a legacy
    // free-text value reaching the resolver would fail validation on a field
    // the operator never touched and block the save entirely.
    care: normalizeCare(p.attributes.care),
  };
}

/**
 * Store admin — Edit product (item master + listing). Commerce V1.0.1 M2.2.
 * On @flint/ui + react-hook-form; SKU is read-only. @see ADR-0048
 */
export function ProductEditPage({ listingId }: { listingId: string }) {
  const q = trpc.storeCatalog.get.useQuery({ listingId });
  const p = q.data;

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Products", to: "/store/admin/products" },
        { label: p?.name ?? "…", to: "/store/admin/products" },
        { label: "Edit" },
      ]}
    >
      {p ? (
        // Keyed + mounted only once the product is here, so every field —
        // Radix `Select` included — initialises from saved data. A form that
        // mounts empty and is reset afterwards leaves the selects behind:
        // Radix can't resolve a value whose item wasn't mounted, clears itself
        // to "", and writes that back through `onValueChange`. That silently
        // emptied Category and made "Unit of sale" fail validation on save.
        <EditProductForm key={p.listingId} product={p} listingId={listingId} />
      ) : (
        <StorePageColumn>
          <div className="flex items-center gap-3">
            <BackButton fallbackTo="/store/admin/products" label="Back to products" />
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              Edit product
            </h1>
          </div>
          {q.isLoading ? (
            <Skeleton className="mt-5 h-40 w-full" />
          ) : (
            <p className="mt-5 text-sm text-muted">Product not found.</p>
          )}
        </StorePageColumn>
      )}
    </StoreAdminLayout>
  );
}

function EditProductForm({ product: p, listingId }: { product: Product; listingId: string }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const update = trpc.storeCatalog.update.useMutation();

  const [images, setImages] = useState<string[]>(p.images);

  // `unit` was a free-text input here before this form used the create form's
  // picker, so a saved product can hold a value the picker doesn't list.
  // Widening the options keeps that value selectable instead of blanking it.

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: toValues(p),
  });

  // Picker vocabulary from the catalog; `current` keeps this product's saved
  // values selectable even if nothing else uses them any more.
  const options = useCatalogOptions({
    category: p.category ?? "",
    subCategory: p.subCategory ?? "",
    // Always empty: the field adds a membership, it does not represent them
    // (spec 15 R5 — a style sits in many line sheets and this control holds
    // one). Prefilling it would read as "the collection", so changing it would
    // read as a move, and the save would silently produce dual membership.
    collection: "",
  });
  const [optionDialog, setOptionDialog] = useState<OptionKind | null>(null);

  // The child picker's list, its placeholder and its dialog all key off the
  // parent the operator has selected right now.
  const category = form.watch("category");
  const subCategoryOptions = options.subCategoriesFor(category);
  useSubCategoryReset(
    category,
    useCallback(() => form.setValue("subCategory", "", { shouldDirty: true }), [form]),
  );

  // Selecting a just-added value has to wait for it to exist as an option:
  // Radix Select falls back to the placeholder when the value matches no item,
  // and won't re-derive the label when the list catches up.
  const [pendingSelect, setPendingSelect] = useState<{ kind: OptionKind; name: string } | null>(
    null,
  );
  useEffect(() => {
    if (pendingSelect && options.has(pendingSelect.kind, pendingSelect.name, category)) {
      form.setValue(pendingSelect.kind, pendingSelect.name, { shouldDirty: true });
      setPendingSelect(null);
    }
  }, [pendingSelect, options, form, category]);

  const vis = useListingVisibility();

  /**
   * The status the product will have after Save — a staged form value like
   * every other field, not an immediate action. Defaults to the saved
   * visibility, so an untouched form saves with no transition at all.
   *
   * This replaced an immediate-apply control (click → transition fires) after
   * operator feedback: a control standing among deferred fields that committed
   * on click was the surprising one on the page. Save is now the single moment
   * anything changes, status included.
   */
  const [statusPick, setStatusPick] = useState<Visibility>(p.visibility);
  const statusChanged = statusPick !== p.visibility;
  const [confirmArchive, setConfirmArchive] = useState(false);
  // Wizard position (user decision 2026-08-13: edit matches create). Every
  // section stays MOUNTED (visibility is CSS) so no component state is lost —
  // the variant matrix's session tombstones above all.
  const [step, setStep] = useState(0);
  // Every step is reachable from the start on EDIT (user decision 2026-08-13):
  // the data already exists, so an operator jumps straight to the field they
  // came to change. The CREATE wizard keeps its guided, visited-steps-only
  // progression.
  const [maxVisited, setMaxVisited] = useState(EDIT_WIZARD_STEPS.length - 1);
  const goToStep = useCallback((i: number) => {
    setStep(i);
    setMaxVisited((m) => Math.max(m, i));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const nextStep = async () => {
    const fields = EDIT_STEP_FIELDS[step] ?? [];
    const ok = fields.length === 0 || (await form.trigger(fields));
    if (ok) {
      goToStep(step + 1);
      return;
    }
    const firstError = fields
      .map((f) => form.getFieldState(f).error?.message)
      .find((m): m is string => Boolean(m));
    toast.error(
      "Fix the highlighted fields to continue",
      firstError ? { description: firstError } : undefined,
    );
  };

  /**
   * The Save path, with the staged status folded in.
   *
   * Publishing is the only transition with server preconditions, so it is the
   * only one that pre-checks — before validation, because the blockers are
   * `publishListing`'s own sentences (@see publishBlockers) and belong on the
   * fields that carry them. Archiving asks first: it pulls the product off the
   * store, and a Save that also does that deserves one explicit yes.
   */
  function handleSave() {
    if (statusChanged && statusPick === "published") {
      const v = form.getValues();
      const blockers = publishBlockers({
        // What will actually be *persisted* — deliberately not `hasPricing(v)`,
        // which also counts a legacy positional sizeRatio the server ignores.
        pricedEntryCount: usableEntries(v.pricingEntries).length,
        hsn: v.hsn.trim() || null,
      });
      if (blockers.length > 0) {
        if (usableEntries(v.pricingEntries).length === 0) {
          form.setError("pricingEntries", { message: NEEDS_PRICE });
        }
        if (!v.hsn.trim()) {
          form.setError("hsn", { message: "Required before publishing" });
        }
        toast.error("Can't publish yet", { description: blockers.join(" ") });
        return;
      }
    }
    if (statusChanged && statusPick === "archived") {
      setConfirmArchive(true);
      return;
    }
    void form.handleSubmit((v) => onSubmit(v, statusChanged ? statusPick : undefined))();
  }

  /**
   * Save, and move the listing afterwards when the staged status changed.
   *
   * `next` is the status the operator picked on the form, passed only when it
   * differs from the saved one. It is applied *after* the update, through the
   * audited transition procedures — visibility is not part of the update
   * payload and never becomes part of it.
   */
  async function onSubmit(v: Values, next?: Visibility) {
    const entries = usableEntries(v.pricingEntries);
    const primary = entries[0];
    // Entries first, legacy count second — the same precedence every other
    // caller uses, so the divisor can't differ from the one the cards showed.
    const pieces = primary?.pieces || effectivePiecesPerPack(v.sizeRatio, v.piecesPerPack);
    // The record stores per piece whatever the primary entry is priced in.
    // @see perPiecePaise
    const ratePaise = primary
      ? primary.kind === "pack"
        ? perPiecePaise(BigInt(primary.pricePaise || "0"), pieces)
        : BigInt(primary.pricePaise || "0")
      : 0n;
    // A **published** product must keep a price — it is on the storefront, and
    // R5 says a live listing carries at least one pack price. A draft may be
    // saved priceless: that is how a product gets named and described before it
    // is priced, and `publishListing` is the floor that stops it going live
    // (AC9). Gated on what the listing *is*, not on which button was clicked —
    // this form has only one.
    if (p.visibility === "published" && (ratePaise <= 0n || !hasPricing(v))) {
      form.setError("pricingEntries", { message: NEEDS_PRICE });
      return;
    }
    const attributes = buildAttributes(v);
    try {
      await update.mutateAsync({
        listingId,
        name: v.name.trim(),
        basePricePaise: ratePaise.toString(),
        // Mirrored from the primary entry server-side; sent as the fallback.
        // Pack, not piece: garments are pack-only since spec 13, and the only
        // way here without a primary is a draft not yet priced.
        discountBps: primary?.discountBps ?? 0,
        soldBy: primary?.kind ?? "pack",
        hsn: v.hsn.trim() ? v.hsn.trim() : null,
        category: v.category.trim() ? v.category.trim() : null,
        subCategory: v.subCategory.trim() ? v.subCategory.trim() : null,
        brand: v.brand.trim() ? v.brand.trim() : null,
        countryOfOrigin: v.countryOfOrigin ? v.countryOfOrigin : null,
        // Always sent: on edit, an emptied builder means "remove the matrix",
        // and only a complete axis reaches the wire (unfinished rows drop).
        variantAttributes: completeAxes(v.variantAttributes),
        variants: variantsToWire(v.variants),
        description: v.description.trim() ? v.description.trim() : null,
        taxRateBps: v.taxPct !== "" ? Math.round(Number(v.taxPct) * 100) : null,
        // MOQ lives on the entry now; the column mirrors the primary.
        moq: primary?.moq ?? null,
        shortDesc: v.shortDesc.trim() ? v.shortDesc.trim() : null,
        longDesc: v.longDesc.trim() ? v.longDesc.trim() : null,
        attributes,
        collectionId: v.collection || null,
        images,
        // The entries are authoritative; the server mirrors `pricingEntries[0]`
        // into the two columns below (ADR-0051) and ignores what we send for
        // them. They still carry the legacy positional fallback.
        pricingEntries: entries,
        piecesPerPack: effectivePiecesPerPack(v.sizeRatio, v.piecesPerPack) || null,
        sizeRatio: v.sizeRatio.trim() ? v.sizeRatio.trim() : null,
      });
      await Promise.all([
        utils.storeCatalog.list.invalidate(),
        utils.storeCatalog.facets.invalidate(),
        utils.storeCatalog.get.invalidate({ listingId }),
        utils.storeCatalog.counts.invalidate(),
      ]);
      toast.success("Changes saved");
    } catch (err) {
      toast.error("Couldn't save changes", {
        description: storeErrorMessage(err),
      });
      return;
    }
    // Its own try, deliberately. Folding this into the one above would report a
    // failed transition as "Couldn't save changes" on a product whose changes
    // *did* save — the operator's next move is to save again, which is both
    // pointless and confusing. Two steps, two outcomes, two honest messages.
    if (next) {
      try {
        await vis.setVisibility(listingId, next);
      } catch {
        // `setVisibility` has already toasted the real reason. Stay on the form
        // so the operator can fix whatever blocked it.
        return;
      }
    }
    await navigate({ to: "/store/admin/products/$listingId", params: { listingId } });
  }

  return (
    <StorePageColumn variant="grid">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BackButton fallbackTo={`/store/admin/products/${listingId}`} label="Back to product" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              Edit product
            </h1>
            <p className="mt-1.5 text-sm text-muted">SKU {p.sku ?? "—"} · immutable</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            intent="secondary"
            size="md"
            disabled={update.isPending}
            onClick={() => navigate({ to: "/store/admin/products/$listingId", params: { listingId } })}
          >
            Cancel
          </Button>
          {/* Only on the final step — mid-wizard the action lives in Review,
              matching the create wizard. */}
          {step === EDIT_WIZARD_STEPS.length - 1 ? (
            <Button
              intent="primary"
              size="md"
              disabled={update.isPending || vis.isPending}
              onClick={handleSave}
            >
              Save changes
            </Button>
          ) : null}
        </div>
      </div>

      {/* Section order, labels, helpers and placeholders deliberately track
          `product-new-page.tsx` field for field, so the two screens read as one
          form. Two intentional departures: SKU is disabled (immutable once used
          on an order) and there is no Inventory step — opening stock is a
          create-time posting to the stock ledger, not an editable property. */}
      <Form {...form}>
        <form className="mx-auto mt-5 w-full" onSubmit={(e) => e.preventDefault()}>
          <div className="flex items-start gap-6">
            <aside className="sticky top-4 hidden w-56 shrink-0 lg:block">
              <ProductWizardStepper
                steps={EDIT_WIZARD_STEPS}
                current={step}
                maxVisited={maxVisited}
                onGo={goToStep}
              />
            </aside>

            <div className="min-w-0 flex-1">
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                  Step {step + 1} of {EDIT_WIZARD_STEPS.length}
                </p>
                <h2 className="mt-1 text-xl font-bold tracking-tight text-ink">
                  {EDIT_WIZARD_STEPS[step]!.label}
                </h2>
                <p className="mt-1 text-sm text-muted">{EDIT_WIZARD_STEPS[step]!.description}</p>
              </div>
          {/* One card PER SECTION (#265), one VISIBLE at a time — the hidden
              wrappers keep everything mounted so no state is lost. */}
          <div className="flex flex-col gap-4">
            <div className={step === 0 ? undefined : "hidden"}>
            <StoreFormSection
              title="Identity"
              help="Name, SKU, HSN, category — how the product is discoverable in lists and reports."
            >
              <StoreFormRow single>
                <TextField name="name" label="Product name *" placeholder="Cotton Round-Neck Tee — Ecru" />
              </StoreFormRow>
              <StoreFormRow>
                <TextField
                  name="sku"
                  label="SKU"
                  disabled
                  helper="Immutable once used on an order."
                />
                <TextField name="hsn" label="HSN code" placeholder="6109" />
              </StoreFormRow>
              <StoreFormRow cols={3}>
                <div>
                  <SelectField
                    name="category"
                    label="Category *"
                    options={options.categories}
                    placeholder={options.placeholderFor("category")}
                  />
                  <AddOptionButton label="New category" onClick={() => setOptionDialog("category")} />
                </div>
                <div>
                  {/* Disabled until there's a parent to file it under — a
                      sub-category exists only as a pairing with a category.
                      Clearable, unlike Category: moving a product back to one
                      level is a legitimate edit. */}
                  <SelectField
                    name="subCategory"
                    label="Sub-category"
                    options={subCategoryOptions}
                    placeholder={options.placeholderFor("subCategory", category)}
                    disabled={!category}
                    // Only offered when there is something to clear back from.
                    // `noneLabel` makes an empty value select the "none" item,
                    // which would otherwise hide the placeholder that tells the
                    // operator to pick a category first (spec 45 AC2).
                    {...(subCategoryOptions.length ? { noneLabel: "Not specified" } : {})}
                  />
                  {category ? (
                    <AddOptionButton
                      label="New sub-category"
                      onClick={() => setOptionDialog("subCategory")}
                    />
                  ) : null}
                </div>
                <div>
                  {/* Adds only, and starts empty. A style can sit in several
                      line sheets (spec 15 R5) and this picker holds one, so it
                      must not be able to speak for the others: prefilled, it
                      would read as "the collection" and changing it would read
                      as a move. Removing is done on the collection, where its
                      order is visible. */}
                  <SelectField
                    name="collection"
                    label="Add to collection"
                    options={options.collections}
                    placeholder={options.placeholderFor("collection")}
                  />
                  <p className="mt-1.5 text-xs text-muted">
                    Adds this product to one more line sheet. To see or change which
                    collections it&rsquo;s already in — and their order — open the collection.
                  </p>
                </div>
              </StoreFormRow>
              <StoreFormRow single>
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          value={field.value ?? ""}
                          rows={3}
                          placeholder="Short marketing copy shown on line sheets and quotes."
                          className={TEXTAREA_CLASS}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </StoreFormRow>
            </StoreFormSection>
            </div>

            <div className={step === 1 ? undefined : "hidden"}>
            <StoreFormSection
              title="Images"
              help="Up to 8. The first image is the primary one shown on line sheets."
            >
              <ProductImagesField value={images} onChange={setImages} />
            </StoreFormSection>
            </div>

            {/* Attributes and Variants sit BEFORE Bundle Builder — the TradeCore
                mockup's step order, and a hard dependency since #247: the bundle
                dialog composes every bundle from the Size/Color axes defined here
                and has no picker to fall back on. */}
            <div className={step === 2 ? undefined : "hidden"}>
            <StoreFormSection
              title="Attributes"
              help="Define the options this product comes in — every combination of values becomes a variant below. Additive to pack pricing; packs stay the unit of sale."
            >
              <FormField
                control={form.control}
                name="variantAttributes"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <AttributeBuilder value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </StoreFormSection>
            </div>

            <div className={step === 3 ? undefined : "hidden"}>
            <StoreFormSection
              title="Variants"
              help="Every attribute combination, generated automatically — what the product comes in. Fine-tune SKU, barcode and weight; stock is derived from the bundles below."
            >
              <FormField
                control={form.control}
                name="variants"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <VariantsField
                        value={field.value}
                        onChange={field.onChange}
                        attributes={form.watch("variantAttributes")}
                        baseSku={form.watch("sku")}
                        pricingEntries={form.watch("pricingEntries") ?? []}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </StoreFormSection>
            </div>

            {/* Bundle Builder is its own card, separate from Variants (#265,
                UX-review finding 1) — same layout as the create form. */}
            <div className={step === 4 ? undefined : "hidden"}>
            <StoreFormSection
              title="Bundle Builder"
              help="Compose the fixed packages a dealer buys — pick variants, set quantities per bundle, price each one. A product can offer several bundles."
            >
              {/* GST first: it applies to every bundle, and each card shows its
                  own incl-GST figure, so the rate has to be answerable before
                  the list means anything. */}
              <StoreFormRow>
                <TextField
                  name="taxPct"
                  label="GST (%)"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  placeholder="5"
                  helper="Any rate — the usual slabs are 0, 5, 12, 18 and 28."
                />
              </StoreFormRow>

              <ProductPackFields />
            </StoreFormSection>
            </div>

            <div className={step === 5 ? undefined : "hidden"}>
            <StoreFormSection
              title="Merchandising"
              help="Copy and attributes shown on the line sheet and the product page."
            >
              <StoreFormRow single>
                <TextField name="shortDesc" label="Short description (catalog teaser)" placeholder="Combed cotton, pre-shrunk" />
              </StoreFormRow>
              <StoreFormRow single>
                <FormField
                  control={form.control}
                  name="longDesc"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Long description (line sheet / storefront)</FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          value={field.value ?? ""}
                          rows={4}
                          placeholder="Full marketing copy shown on the product page."
                          className={TEXTAREA_CLASS}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </StoreFormRow>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField name="brand" label="Brand" placeholder="Loomwell" />
                <SelectField
                  name="countryOfOrigin"
                  label="Country of origin"
                  options={COUNTRY_OPTIONS}
                  noneLabel="Not specified"
                />
                {/* The staged Status control moved to the Review & Save step
                    with the edit wizard — beside the button that commits it. */}
                <TextField name="material" label="Material / fabric" placeholder="100% cotton" />
                <TextField name="gsm" label="GSM" placeholder="180" />
                <TextField name="colour" label="Colour" placeholder="Ecru" />
                <SelectField
                  name="care"
                  label="Care"
                  options={CARE_OPTIONS}
                  noneLabel="Not specified"
                />
              </div>
            </StoreFormSection>
            </div>

            {step === EDIT_WIZARD_STEPS.length - 1 ? (
              <ProductReviewStep
                imagesCount={images.length}
                showInventory={false}
                fieldStep={EDIT_FIELD_STEP}
                errors={Object.entries(form.formState.errors).map(([field, err]) => ({
                  field,
                  message: (err as { message?: string })?.message ?? `Check ${field}`,
                }))}
                onJump={goToStep}
                footer={
                  <div className="mt-1 flex flex-wrap items-end justify-between gap-4 rounded border border-line bg-paper px-4 py-3 shadow">
                    {/* Staged like every other field: the pick is committed by
                        Save, which routes it through the audited
                        publish/unpublish/archive transitions — the `update`
                        payload never carries visibility, and the permission
                        split (`store.catalog.publish` vs `.manage`) is
                        enforced by disabling the segments. */}
                    <StatusField
                      current={statusPick}
                      disabled={vis.isPending || update.isPending}
                      canChange={vis.canChange}
                      onPick={setStatusPick}
                    />
                    <Button
                      intent="primary"
                      size="md"
                      disabled={update.isPending || vis.isPending}
                      onClick={handleSave}
                    >
                      Save changes
                    </Button>
                  </div>
                }
              />
            ) : null}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <Button
              intent="secondary"
              size="md"
              type="button"
              disabled={step === 0}
              onClick={() => goToStep(step - 1)}
            >
              <ArrowLeft className="size-4" aria-hidden />
              Back
            </Button>
            {step < EDIT_WIZARD_STEPS.length - 1 ? (
              <Button intent="primary" size="md" type="button" onClick={() => void nextStep()}>
                Next
                <ArrowRight className="size-4" aria-hidden />
              </Button>
            ) : null}
          </div>
            </div>

            <aside className="sticky top-4 hidden w-72 shrink-0 xl:block">
              <ProductPreviewRail
                images={images}
                createAs={statusPick === "published" ? "published" : "draft"}
                showOpeningStock={false}
              />
            </aside>
          </div>
        </form>
      </Form>

      {optionDialog ? (
        <NewOptionDialog
          kind={optionDialog}
          open
          onOpenChange={(next) => !next && setOptionDialog(null)}
          // Sub-category duplicates are only duplicates under the same parent —
          // "Full sleeve" may exist under both Shirts and Jackets.
          existing={optionsForDialog(optionDialog, options, category).map((o) => o.value)}
          onCreate={({ name }) => {
            options.add(optionDialog, name, category);
            setPendingSelect({ kind: optionDialog, name });
            toast.success(
              `${optionDialog === "subCategory" ? "Sub-category" : "Category"} “${name}” added`,
              { description: "Applied to this product. It is saved when you save the product." },
            );
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirmArchive}
        onOpenChange={setConfirmArchive}
        title="Archive product?"
        description={`“${p.name}” will be pulled off the store, and your edits will be saved. You can republish it later.`}
        confirmLabel="Save & archive"
        pending={vis.isPending || update.isPending}
        onConfirm={async () => {
          // Through `handleSubmit` so the resolver still runs.
          //
          // The second callback is not optional politeness. `handleSubmit`
          // resolves whether validation passed or failed, so without it a
          // blocked save closes this dialog having done nothing at all and says
          // nothing about why — a dead button. The errors it sets are on fields
          // behind the dialog, which is why this closes rather than staying up.
          await form.handleSubmit(
            (v) => onSubmit(v, "archived"),
            () =>
              toast.error("Fix the highlighted fields first", {
                description: "The product can't be saved as it stands, so it wasn't archived.",
              }),
          )();
          setConfirmArchive(false);
        }}
      />
    </StorePageColumn>
  );
}

/**
 * The status control: `StatusSegments` styled as a field (label above,
 * bordered control below) so it reads alongside Country of origin — the
 * mockup this follows pairs the two.
 *
 * Staged, like every other field on the form: the pick is held in state and
 * committed by Save (`handleSave` folds it into `onSubmit`), which routes the
 * change through the audited transitions rather than the `update` payload —
 * visibility never rides the generic update. It rendered as immediate-apply
 * in its first iteration; the operator read that as the one field on the page
 * that fired without Save, and they were right to.
 *
 * Disabled rather than hidden without `store.catalog.publish`, because the
 * control doubles as the status display: hiding it would leave an operator
 * editing a product with no way to see whether it's live.
 */
function StatusField({
  current,
  disabled,
  canChange,
  onPick,
}: {
  current: Visibility;
  disabled: boolean;
  canChange: boolean;
  onPick: (to: Visibility) => void;
}) {
  const group = (
    <StatusSegments current={current} onPick={onPick} disabled={disabled || !canChange} />
  );

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium leading-[18px] text-ink-2">Status</span>
      {canChange ? (
        group
      ) : (
        // The `<span>` is load-bearing: disabled buttons fire no pointer
        // events, so without a wrapper the tooltip never opens.
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{group}</span>
          </TooltipTrigger>
          <TooltipContent>Needs catalog publish permission</TooltipContent>
        </Tooltip>
      )}
      <p className="text-xs text-muted">Applied when you save changes.</p>
    </div>
  );
}
